import { useEffect, useState } from "react";
import { CheckCircle2, ScanLine, AlertTriangle, Ban } from "lucide-react";
import { PhotoCapture } from "./Camera";
import { BigButton, StepTitle } from "./KycShell";
import { StepManual } from "./StepManual";

const SIDES = [
  { tipo: "doc_frente", title: "Foto del frente de tu documento", hint: "Pon el documento dentro del marco, sobre una mesa, con buena luz y sin reflejos." },
  { tipo: "doc_reverso", title: "Ahora el reverso (la parte de atrás)", hint: "Da vuelta el documento y ponlo dentro del marco." },
];

const Leyendo = () => {
  const [seg, setSeg] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSeg((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-seg / 12))));
  return (
    <div className="text-center space-y-5 py-10" data-testid="kyc-leyendo">
      <ScanLine size={56} className="mx-auto text-slate-700 animate-pulse" />
      <h2 className="font-heading text-2xl font-bold">Estamos leyendo tu documento</h2>
      <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-slate-900 transition-[width] duration-1000" style={{ width: `${pct}%` }} data-testid="kyc-leyendo-progreso" />
      </div>
      <p className="text-base text-slate-500">Esto puede tardar unos segundos. No cierres esta página.</p>
    </div>
  );
};

const Fallo = ({ intento, max, color, onRetry }) => (
  <div className="space-y-5" data-testid="kyc-lectura-fallida">
    <AlertTriangle size={48} className="text-amber-600" />
    <StepTitle paso="" title="No pudimos leer tu documento">Toma las fotos de nuevo: con buena luz, sin reflejos, el documento completo dentro del marco y bien enfocado.</StepTitle>
    <p className="text-base text-slate-600" data-testid="kyc-lectura-intento">Intento {intento} de {max}</p>
    <BigButton color={color} onClick={onRetry} data-testid="kyc-lectura-reintentar">Tomar las fotos de nuevo</BigButton>
  </div>
);

export const Bloqueo = ({ b }) => (
  <div className="text-center space-y-5 py-8" data-testid="kyc-bloqueo">
    <Ban size={56} className="mx-auto text-rose-600" />
    <h2 className="font-heading text-2xl font-bold" data-testid="kyc-bloqueo-mensaje">{b.mensaje}</h2>
    <p className="text-base text-slate-500">No podemos continuar con esta verificación.</p>
  </div>
);

export const StepDocumento = ({ brand, upload, leer, manual, onDone, paso }) => {
  const [i, setI] = useState(0);
  const [fase, setFase] = useState("foto");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const side = SIDES[i];

  const handle = (r) => {
    if (!r) return setFase("foto");
    setRes(r);
    if (r.ok) return onDone(r.datos);
    setFase(r.bloqueo ? "bloqueo" : r.manual ? "manual" : "fallo");
  };
  const confirm = async (blob, meta) => {
    setBusy(true);
    const ok = await upload(side.tipo, blob, meta);
    setBusy(false);
    if (!ok) return;
    if (i === 0) return setI(1);
    setFase("leyendo");
    handle(await leer());
  };
  const enviarManual = async (body) => {
    setBusy(true);
    const r = await manual(body);
    setBusy(false);
    if (r) handle(r);
  };

  if (fase === "leyendo") return <Leyendo />;
  if (fase === "bloqueo") return <Bloqueo b={res.bloqueo} />;
  if (fase === "fallo") return <Fallo intento={res.intento} max={res.max} color={brand.color} onRetry={() => { setI(0); setFase("foto"); }} />;
  if (fase === "manual") return <StepManual brand={brand} busy={busy} onSubmit={enviarManual} />;
  return (
    <div data-testid={`kyc-step-${side.tipo}`}>
      <StepTitle paso={paso} title={side.title} />
      {i > 0 && <p className="flex items-center gap-2 text-emerald-700 text-base mb-4"><CheckCircle2 size={20} /> Frente listo</p>}
      <PhotoCapture key={`${side.tipo}-${res?.intento || 0}`} facing="environment" frame="documento" hint={side.hint} color={brand.color} onConfirm={confirm} busy={busy} />
    </div>
  );
};
